import { Controller } from "@hotwired/stimulus"

// Rotates the Academy Home rail's promo banners (TMS-1449), crossfading every
// `interval` ms. Only mounted when two or more banners are visible.
//
// WCAG 2.2.2: rotation waits while the pointer is over the card or a keyboard
// focus is inside it, and resumes on leaving. Using the arrows or dots stops it
// for good (the pause button can restart it). With reduced motion there is no
// rotation at all — the first banner shows and the manual controls still work.
export default class extends Controller {
  static targets = ["viewport", "slide", "dot", "controls", "playToggle"]
  static values = { interval: { type: Number, default: 5000 } }

  connect() {
    this.index = 0
    this.hovering = false
    this.focusWithin = false
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.playing = !this.reducedMotion

    // The server hides all but the first slide for no-JS; from here on the
    // `is-active` class drives the crossfade instead.
    this.slideTargets.forEach((slide) => { slide.hidden = false })
    this.controlsTarget.hidden = false
    if (this.reducedMotion && this.hasPlayToggleTarget) this.playToggleTarget.hidden = true

    this.render()
    this.schedule()
  }

  disconnect() {
    this.clearTimer()
  }

  previous() {
    this.takeManualControl()
    this.goTo(this.index - 1)
  }

  next() {
    this.takeManualControl()
    this.goTo(this.index + 1)
  }

  show({ params: { index } }) {
    this.takeManualControl()
    this.goTo(index)
  }

  // Starting is explicit, so it overrides the hover/focus hold the learner is
  // necessarily in while pressing the button; entering again re-applies it.
  togglePlayback() {
    this.playing = !this.playing
    if (this.playing) {
      this.hovering = false
      this.focusWithin = false
    }
    this.render()
    this.schedule()
  }

  pause(event) {
    if (event.type === "mouseenter") this.hovering = true
    // A mouse click focuses a button too; only a keyboard focus should hold it.
    if (event.type === "focusin" && event.target.matches(":focus-visible")) this.focusWithin = true
    this.schedule()
  }

  resume(event) {
    if (event.type === "mouseleave") this.hovering = false
    if (event.type === "focusout" && !this.element.contains(event.relatedTarget)) this.focusWithin = false
    this.schedule()
  }

  // private

  takeManualControl() {
    this.playing = false
  }

  goTo(index) {
    const count = this.slideTargets.length
    this.index = ((index % count) + count) % count
    this.render()
    this.schedule()
  }

  render() {
    this.slideTargets.forEach((slide, index) => {
      const active = index === this.index
      slide.classList.toggle("is-active", active)
      slide.inert = !active
      slide.setAttribute("aria-hidden", String(!active))
    })

    this.dotTargets.forEach((dot, index) => {
      if (index === this.index) {
        dot.setAttribute("aria-current", "true")
      } else {
        dot.removeAttribute("aria-current")
      }
    })

    // Announce banner changes only when the learner is the one making them.
    this.viewportTarget.setAttribute("aria-live", this.playing ? "off" : "polite")

    if (this.hasPlayToggleTarget) {
      this.playToggleTarget.setAttribute("aria-label", this.playing ? "Pause rotation" : "Start rotation")
      const icon = this.playToggleTarget.querySelector("i")
      icon?.classList.toggle("ph-pause", this.playing)
      icon?.classList.toggle("ph-play", !this.playing)
    }
  }

  // (Re)starts the countdown to the next banner when rotation should be
  // running, and clears it otherwise. Every change restarts the full interval.
  schedule() {
    this.clearTimer()
    if (!this.playing || this.hovering || this.focusWithin) return

    this.timer = setTimeout(() => this.goTo(this.index + 1), this.intervalValue)
  }

  clearTimer() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }
}
